import { useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import { Icon } from '../Icon';
import { Progress } from '../Progress';
import { Text } from '../Text';

interface StepperProps {
  readonly index?: number;
  readonly steps?: string[];
  readonly className?: string;
  readonly onClick?: (index: number) => void;
}

const STEPS: string[] = [];

function Stepper({ index = -1, steps = STEPS, onClick, className }: StepperProps) {
  const stepsMax = steps.length - 1;
  const stepsValue = useMemo(() => {
    return steps.map((title, i) => {
      return {
        title,
        onClickHandler() {
          onClick?.(i);
        },
      };
    });
  }, [steps, onClick]);

  return (
    <div className={twMerge('relative flex flex-row', className)}>
      {index >= 0 ? (
        <Progress
          value={Math.min(1, (index + 0.5) / stepsMax)}
          className="absolute top-3 w-full"
          style={{
            width: `${100 * (stepsMax / (stepsMax + 1))}%`,
            left: `${(1 - stepsMax / (stepsMax + 1)) * 50}%`,
          }}
        />
      ) : null}
      {stepsValue.map(({ title, onClickHandler }, i) => {
        const isActive = index > i;
        const isSelected = i === index;

        return (
          <div
            key={i}
            role="button"
            tabIndex={0}
            onKeyUp={onClickHandler}
            onClick={onClickHandler}
            className="relative z-10 flex flex-1 basis-10 flex-col items-center justify-start"
          >
            <div
              className={twMerge(
                'mb-2 flex h-6 w-6 items-center justify-center rounded-full transition-colors delay-200',
                isActive || isSelected ? 'bg-brand' : 'bg-neutral-400',
              )}
            >
              <div
                className={twMerge(
                  'relative flex h-full w-full items-center justify-center transition-transform duration-500 view-transition-[none] transform-style-3d',
                  isActive ? 'rotate-y-[180]' : '',
                )}
              >
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
                  <Text color="white" weight="bold" variant="body-2">
                    {i + 1}
                  </Text>
                </div>
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center backface-hidden rotate-y-[180]">
                  <div className="flex h-[14px] w-[14px] items-center justify-center rounded-full bg-white">
                    <Icon icon="checkmark" color="blue" size="xs" />
                  </div>
                </div>
              </div>
            </div>
            <Text
              weight={isSelected ? 'semibold' : 'normal'}
              variant="caption-2"
              className={isSelected ? 'text-neutral-700' : 'text-neutral-500'}
            >
              {title}
            </Text>
          </div>
        );
      })}
    </div>
  );
}

export { Stepper };
